// src/components/hero/ctl-hero.js
//
// Headline + subhead + CTAs + optional background media.
// Slots are the primary extension mechanism to avoid API drift.
//
// Usage:
// <ctl-hero headline="Reserve online in minutes" subhead="Fast checkout. No phone calls." align="left" size="lg" overlay>
//   <img slot="media" .../>
//   <div slot="actions"><ctl-button>Reserve</ctl-button></div>
//   <div slot="content">...</div>   <!-- optional extra content block under text -->
// </ctl-hero>
//
// Gradient background (no image):
// <ctl-hero headline="Title" subhead="Description" no-media no-scrim style="--ctl-hero-background: linear-gradient(135deg, #2c5282 0%, #2d3748 100%);">
//   <div slot="actions"><ctl-button>CTA</ctl-button></div>
// </ctl-hero>
//
// Attributes:
// - headline: Main headline text
// - subhead: Subheadline/description text  
// - align: "left" | "center" | "right" (default: left)
// - size: "sm" | "md" | "lg" (default: md)
// - overlay: Enable scrim overlay (for use with media/image)
// - no-media: Hide media slot container (for gradient/color backgrounds)
// - no-scrim: Disable scrim overlay
// - style="--ctl-hero-background": Set gradient or solid background

import "../foundation/ctl-button.js";
import "../foundation/ctl-link.js";
import "../foundation/ctl-typography.js";

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    --font: var(--ctl-sf-font, var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial));
    --text: var(--ctl-sf-text, var(--ctl-color-text));
    --muted: var(--ctl-sf-muted, var(--ctl-color-muted));
    --surface: var(--ctl-sf-surface, var(--ctl-color-surface));
    --border: var(--ctl-sf-border, var(--ctl-color-border));
    --brand: var(--ctl-sf-brand, var(--ctl-color-brand));
    --radius: var(--ctl-sf-radius, var(--ctl-radius-lg, 18px));
    --shadow: var(--ctl-sf-shadow, var(--ctl-shadow-sm));
    --focus: var(--ctl-sf-focus-ring, var(--ctl-focus-ring));

    --hero-min: calc(var(--ctl-space-7) * 7.08);
    --hero-pad: var(--ctl-font-size-xl);
    --hero-gap: var(--ctl-space-3-5);

    display:block;
    font-family: var(--font);
    color: var(--text);
  }

  .hero{
    position: relative;
    border-radius: var(--radius);
    overflow:hidden;
    border: var(--ctl-border-width-thin) solid var(--border);
    background: var(--ctl-hero-background, var(--surface));
    box-shadow: var(--shadow);
    min-height: var(--hero-min);
  }

  /* Support for no-media attribute - removes media container when not needed */
  :host([no-media]) .media{
    display: none;
  }

  /* Support for no-scrim attribute - removes scrim overlay */
  :host([no-scrim]) .scrim{
    display: none;
  }

  .media{
    position:absolute;
    inset: 0;
    z-index: var(--ctl-z-layer-0);
    background: color-mix(in srgb, var(--surface) 92%, var(--ctl-color-black) 8%);
  }
  .media ::slotted(img),
  .media ::slotted(video){
    width: 100%;
    height: 100%;
    object-fit: cover;
    display:block;
  }

  .scrim{
    position:absolute;
    inset: 0;
    z-index: var(--ctl-z-layer-1);
    pointer-events:none;
    opacity: 0;
    transition: opacity var(--ctl-transition-normal);
  }
  :host([overlay]) .scrim{ opacity: 1; }

  /* Default scrim is subtle; you can override via --ctl-hero-scrim */
  .scrim{
    background: var(--ctl-hero-scrim, linear-gradient(
      90deg,
      var(--ctl-color-overlay-8) 0%,
      var(--ctl-color-overlay-7) 35%,
      color-mix(in srgb, var(--ctl-color-black) 12%, transparent) 60%,
      transparent 100%
    ));
  }

  .inner{
    position: relative;
    z-index: var(--ctl-z-layer-2);
    padding: var(--hero-pad);
    display:grid;
    gap: var(--hero-gap);
    align-content: start;
  }

  /* Layout variants */
  :host([layout="split"]) .inner{
    grid-template-columns: 1.1fr 0.9fr;
    align-items: start;
  }

  .text{
    min-width: 0;
  }

  .eyebrow{
    display:none;
    font-size: var(--ctl-font-size-sm);
    letter-spacing: var(--ctl-tracking-wider);
    text-transform: uppercase;
    font-weight: var(--ctl-font-weight-black);
    color: color-mix(in srgb, var(--brand) 75%, var(--text));
  }
  :host([eyebrow]) .eyebrow{ display:block; }

  .headline{
    margin: 0;
    font-weight: var(--ctl-font-weight-superblack);
    letter-spacing: var(--ctl-tracking-tight);
    line-height: var(--ctl-leading-tight);
    color: var(--text);
    text-wrap: balance;
  }

  .subhead{
    margin: 0;
    color: var(--muted);
    line-height: var(--ctl-leading-normal);
    font-size: var(--ctl-font-size-base);
    max-width: 68ch;
  }

  :host([overlay]) .headline,
  :host([overlay]) .subhead,
  :host([overlay]) .eyebrow{
    color: var(--ctl-color-white);
    text-shadow: 0 var(--ctl-space-0-5) var(--ctl-font-size-xl) color-mix(in srgb, var(--ctl-color-black) 28%, transparent);
  }
  :host([overlay]) .subhead{
    color: color-mix(in srgb, var(--ctl-color-white) 86%, transparent);
  }

  /* sizes */
  :host([size="sm"]){ --hero-min: calc(var(--ctl-space-7) * 5.42); }
  :host([size="md"]){ --hero-min: calc(var(--ctl-space-7) * 6.67); }
  :host([size="lg"]){ --hero-min: calc(var(--ctl-space-7) * 8.75); }

  :host([size="sm"]) .headline{ font-size: calc(var(--ctl-space-5) + var(--ctl-space-0-5)); }
  :host([size="md"]) .headline{ font-size: calc(var(--ctl-space-6) + var(--ctl-space-0-5)); }
  :host([size="lg"]) .headline{ font-size: calc(var(--ctl-space-6) + var(--ctl-space-3)); }

  :host([size="lg"]) .subhead{ font-size: var(--ctl-font-size-lg); }

  /* alignment */
  .text{ text-align: left; }
  :host([align="center"]) .text{ text-align: center; }
  :host([align="right"]) .text{ text-align: right; }

  :host([align="center"]) .subhead{ margin-left:auto; margin-right:auto; }
  :host([align="right"]) .subhead{ margin-left:auto; }

  .actions{
    display:flex;
    gap: var(--ctl-space-2-5);
    flex-wrap: wrap;
    align-items:center;
    margin-top: var(--ctl-space-1);
  }

  :host([align="center"]) .actions{ justify-content:center; }
  :host([align="right"]) .actions{ justify-content:flex-end; }

  .actions:empty{ display:none; }

  .content{
    margin-top: var(--ctl-space-1);
  }
  .content:empty{ display:none; }

  .aside{
    display:block;
    justify-self: end;
    max-width: calc(var(--ctl-space-7) * 10.83);
  }
  :host([layout="stack"]) .aside{ justify-self: stretch; }

  /* If overlay + split, ensure aside doesn't disappear into image */
  :host([overlay]) .aside{
    filter: drop-shadow(0 var(--ctl-space-2-5) var(--ctl-space-5) color-mix(in srgb, var(--ctl-color-black) 18%, transparent));
  }

  /* Responsive — var(--hero-bp-split) = 900px; CSS custom properties cannot be used in @media expressions */
  @media (max-width: 900px){
    :host([layout="split"]) .inner{
      grid-template-columns: 1fr;
    }
    .aside{
      justify-self: stretch;
      max-width: none;
    }
  }

  /* hide template nodes */
  ::slotted(template){ display:none; }
</style>

<section class="hero" part="hero">
  <div class="media" part="media">
    <slot name="media"></slot>
  </div>
  <div class="scrim" part="scrim" aria-hidden="true"></div>

  <div class="inner" part="inner">
    <div class="text" part="text">
      <div class="eyebrow" part="eyebrow" id="eyebrow"></div>
      <h1 class="headline" part="headline" id="headline"></h1>
      <p class="subhead" part="subhead" id="subhead"></p>

      <div class="actions" part="actions">
        <slot name="actions"></slot>
      </div>

      <div class="content" part="content">
        <slot name="content"></slot>
      </div>
    </div>

    <div class="aside" part="aside">
      <slot name="aside"></slot>
    </div>
  </div>
</section>
`;

function clampStr(s){ return String(s ?? "").trim(); }

export class CtlHero extends HTMLElement {
  static get observedAttributes() {
    return ["headline", "subhead", "eyebrow-text", "size", "align", "layout", "overlay"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._headline = this.shadowRoot.getElementById("headline");
    this._subhead = this.shadowRoot.getElementById("subhead");
    this._eyebrow = this.shadowRoot.getElementById("eyebrow");
  }

  connectedCallback() {
    if (!this.hasAttribute("size")) this.setAttribute("size", "md");
    if (!this.hasAttribute("align")) this.setAttribute("align", "left");
    if (!this.hasAttribute("layout")) this.setAttribute("layout", "stack");
    if (!this.hasAttribute("headline")) this.setAttribute("headline", "Headline");
    if (!this.hasAttribute("subhead")) this.setAttribute("subhead", "Subheadline");
    this._sync();
  }

  attributeChangedCallback() {
    this._sync();
  }

  get headline(){ return this.getAttribute("headline") || ""; }
  set headline(v){ this.setAttribute("headline", String(v ?? "")); }

  get subhead(){ return this.getAttribute("subhead") || ""; }
  set subhead(v){ this.setAttribute("subhead", String(v ?? "")); }

  get eyebrowText(){ return this.getAttribute("eyebrow-text") || ""; }
  set eyebrowText(v){ this.setAttribute("eyebrow-text", String(v ?? "")); }

  get size(){ return this.getAttribute("size") || "md"; }
  set size(v){
    const next = (v === "sm" || v === "lg") ? v : "md";
    this.setAttribute("size", next);
  }

  get align(){ return this.getAttribute("align") || "left"; }
  set align(v){
    const next = (v === "center" || v === "right") ? v : "left";
    this.setAttribute("align", next);
  }

  get layout(){ return this.getAttribute("layout") || "stack"; }
  set layout(v){
    const next = (v === "split" || v === "stack") ? v : "stack";
    this.setAttribute("layout", next);
  }

  get overlay(){ return this.hasAttribute("overlay"); }
  set overlay(v){ v ? this.setAttribute("overlay","") : this.removeAttribute("overlay"); }

  _sync() {
    this._headline.textContent = clampStr(this.headline);
    this._subhead.textContent = clampStr(this.subhead);

    const eb = clampStr(this.eyebrowText);
    if (eb) {
      this.setAttribute("eyebrow", "");
      this._eyebrow.textContent = eb;
    } else {
      this.removeAttribute("eyebrow");
      this._eyebrow.textContent = "";
    }
  }
}

customElements.define("ctl-hero", CtlHero);
